/**
 * Fatiado de pages/ImportMenu.tsx por responsabilidade (#131, R3).
 */
import { useRef, useState } from 'react'
import { imageToJpeg } from './prepare.js'
import { importsApi } from '../../lib/api.js'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ImportQuickReply, MenuImportView } from '../../lib/api.js'
import { useI18n } from '../../lib/i18n.js'
import { Button, toast } from '../../components/ui/index.js'

// ─── Chat ─────────────────────────────────────────────────────────────────────

export function ImportChat({ view }: { view: MenuImportView }) {
  const { t } = useI18n()
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const chatLive = view.status === 'processing'
  const photoInput = useRef<HTMLInputElement>(null)
  const lastPhotoRequest = [...view.messages]
    .reverse()
    .find((m) => m.kind === 'photo_request' && m.photo_page !== null)
  const targetPage = view.awaiting === 'photo' ? (lastPhotoRequest?.photo_page ?? 1) : 1

  const msgMut = useMutation({
    mutationFn: (message: string) => importsApi.message(view.id, message),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['menu-import', view.id] }),
    onError: (e: Error) => toast.error(e.message),
  })

  const allOkFromChat = useMutation({
    mutationFn: () => importsApi.markAllReviewed(view.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['menu-import', view.id] }),
    onError: (e: Error) => toast.error(e.message),
  })

  const pageMut = useMutation({
    mutationFn: (blob: Blob) => importsApi.sendPage(view.id, targetPage, blob),
    onSuccess: () => {
      toast.success(t('admin.import.replaced_page', { n: String(targetPage) }))
      void qc.invalidateQueries({ queryKey: ['menu-import', view.id] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const send = () => {
    const message = text.trim()
    if (!message) return
    setText('')
    msgMut.mutate(message)
  }

  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3 max-h-96 overflow-y-auto">
        {view.messages.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">{t('admin.import.start')}</p>
        )}
        {view.messages.map((m) => (
          <div
            data-testid={`import-chat-message-${m.id}`}
            key={m.id}
            className={m.role === 'assistant' ? 'flex justify-start' : 'flex justify-end'}
          >
            <div
              className={`rounded-2xl px-3 py-2 text-sm max-w-[85%] ${
                m.role === 'assistant'
                  ? 'bg-emerald-50 text-gray-800 border border-emerald-100'
                  : 'bg-orange-500 text-pratzu-aubergine'
              }`}
            >
              {m.kind === 'photo_request' && <span className="mr-1">📷</span>}
              {m.content}
              {m.kind === 'photo_request' && m.photo_page !== null && (
                <p className="mt-1 text-xs opacity-70">p. {m.photo_page}</p>
              )}
              {m.quick_replies && m.quick_replies.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {m.quick_replies.map((qr, i) => {
                    const q =
                      typeof qr === 'string'
                        ? ({ label: qr, action: 'reply' } as ImportQuickReply)
                        : qr
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() =>
                          q.action === 'all_ok'
                            ? allOkFromChat.mutate()
                            : msgMut.mutate(q.value ?? q.label)
                        }
                        className="rounded-full border border-emerald-300 bg-white text-gray-700 text-xs px-2 min-h-11 inline-flex items-center hover:bg-emerald-50"
                      >
                        {q.label}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          data-testid="import-chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder={
            chatLive ? t('admin.import.chat_placeholder') : t('admin.import.chat_done_hint')
          }
          disabled={!chatLive}
          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
        />
        <Button
          data-testid="import-chat-send"
          onClick={send}
          loading={msgMut.isPending}
          disabled={!chatLive}
        >
          ➤
        </Button>
        {chatLive && (
          <Button
            variant="secondary"
            data-testid="import-chat-photo"
            onClick={() => photoInput.current?.click()}
          >
            📷
          </Button>
        )}
        <input
          data-testid="import-chat-photo-input"
          ref={photoInput}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0]
            if (!file) return
            pageMut.mutate(await imageToJpeg(file))
            e.target.value = ''
          }}
        />
      </div>
    </div>
  )
}
